import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import { Input, Row, Label } from 'reactstrap';
import { Colxx } from '../../../../components/common/CustomBootstrap';
import CustomSelectInput from '../../../../components/common/CustomSelectInput';

const QuickWashPricing = ({ update, defaultValue, selectedModifiers = [] }) => {
    const { modifiers } = useSelector(state => state.providers);
    const [quickWashPricing, setQuickWashPricing] = useState(defaultValue || {});
    
    useEffect(() => {
        setQuickWashPricing(defaultValue || {});
    }, [defaultValue]);

    // Clean up pricing data for removed modifiers
    useEffect(() => {
        setQuickWashPricing(prevPricing => {
            if (!prevPricing || Object.keys(prevPricing).length === 0) return prevPricing;
            
            const selectedModifierSet = new Set(selectedModifiers);
            const cleanedPricing = {};
            let hasChanges = false;
            
            // Only keep modifier keys that are still in selectedModifiers
            Object.keys(prevPricing).forEach(key => {
                // Check if this key is a modifier ID (exists in modifiers collection)
                // If it's a modifier and not in selectedModifiers, skip it
                if (modifiers?.[key]) {
                    if (selectedModifierSet.has(key)) {
                        cleanedPricing[key] = prevPricing[key];
                    } else {
                        hasChanges = true;
                    }
                } else {
                    // Keep non-modifier keys (if any exist)
                    cleanedPricing[key] = prevPricing[key];
                }
            });
            
            // Only update if something was actually removed
            if (hasChanges) {
                update(cleanedPricing);
                return cleanedPricing;
            }
            return prevPricing;
        });
    }, [selectedModifiers, modifiers, update]);

    const handleModifierPriceChange = (modifierId, washType, value) => {
        const updatedPricing = {
            ...quickWashPricing,
            [modifierId]: {
                ...(quickWashPricing?.[modifierId] || {}),
                [washType]: value
            }
        };
        setQuickWashPricing(updatedPricing);
        update(updatedPricing);
    }

    const handleModifierDurationChange = (modifierId, durationType, value) => {
        const updatedPricing = {
            ...quickWashPricing,
            [modifierId]: {
                ...(quickWashPricing?.[modifierId] || {}),
                [durationType]: value
            }
        };
        setQuickWashPricing(updatedPricing);
        update(updatedPricing);
    }

    // Generate duration options (00:15 to 06:30 in 15-minute increments)
    const durationOptions = (() => {
        const options = [];
        let startDuration = '00:15';
        for (let i = 0; i < 26; i++) {
            options.push({
                label: startDuration,
                value: startDuration,
                key: i + 1
            });
            startDuration = moment(startDuration, 'HH:mm').add(15, 'minutes').format('HH:mm');
        }
        return options;
    })();

    const washTypes = [
        { 
            key: 'price', 
            value: 'Normal Quick Wash Price',
            durationKey: 'duration',
            durationLabel: 'Normal Quick Wash Duration'
        },
        { 
            key: 'waterlessPrice', 
            value: 'Waterless Quick Wash Price',
            durationKey: 'waterlessDuration',
            durationLabel: 'Waterless Quick Wash Duration'
        },
        { 
            key: 'steamWashPrice', 
            value: 'Steam Quick Wash Price',
            durationKey: 'steamWashDuration',
            durationLabel: 'Steam Quick Wash Duration'
        }
    ];

    // Get modifier details for selected modifiers
    const selectedModifierDetails = selectedModifiers
        .map(modifierId => modifiers?.[modifierId])
        .filter(Boolean);

    return (
        <div>
            {selectedModifierDetails.length > 0 ? (
                <>
                    {selectedModifierDetails.map((modifier) => (
                        <div key={modifier.key} className="mb-4" style={{ 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '8px', 
                            padding: '20px',
                            backgroundColor: '#f9f9f9'
                        }}>
                            <Label className="mb-3" style={{ 
                                fontSize: '16px', 
                                fontWeight: '600',
                                color: '#333',
                                display: 'block'
                            }}>
                                {modifier.name} {modifier.name_ar && `(${modifier.name_ar})`}
                            </Label>
                            <Row>
                                {washTypes.map((washType) => (
                                    <Colxx key={washType.key} xs="12" sm="6" md="4">
                                        <div className='mb-2' style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                                            {washType.value}
                                        </div>
                                        <Input 
                                            type="number" 
                                            placeholder="Price"
                                            value={quickWashPricing?.[modifier.key]?.[washType.key] || ''}
                                            onChange={(e) => handleModifierPriceChange(modifier.key, washType.key, e.target.value)}
                                            className="mb-3"
                                        />
                                        <div className='mb-2' style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                                            {washType.durationLabel}
                                        </div>
                                        <Select
                                            components={{ Input: CustomSelectInput }}
                                            className="react-select"
                                            name="form-field-name"
                                            value={quickWashPricing?.[modifier.key]?.[washType.durationKey] ? { 
                                                value: quickWashPricing[modifier.key][washType.durationKey], 
                                                label: quickWashPricing[modifier.key][washType.durationKey] 
                                            } : ''}
                                            placeholder="e.g. 00:15"
                                            onChange={(e) => handleModifierDurationChange(modifier.key, washType.durationKey, e?.value || '')}
                                            options={durationOptions}
                                        />
                                    </Colxx>
                                ))}
                            </Row>
                        </div>
                    ))}
                </>
            ) : (
                <div className="mb-4" style={{ 
                    padding: '15px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404'
                }}>
                    Please select modifiers in the General Information section to configure pricing.
                </div>
            )}
        </div>
    );
};

export default QuickWashPricing;